/**
 * Tour Route Resolver
 *
 * beforeEnter navigation guard for /tours/:id.
 * Fetches the GuidedTour's outline and redirects to MainMap. A tour that plays
 * in the docked reader (`playsInReader`) opens there for the viewer; every
 * other tour (a product walkthrough, a context replay) resolves its parent
 * DiscussionTopic and opens the We tab on it.
 *
 * This is NOT a page component — the route always redirects.
 *
 * Auth gating is handled by the route's meta.requiresAuth flag,
 * which the global beforeEach guard enforces (redirects to SignIn
 * with return-to URL preserved). By the time beforeEnter fires,
 * the user is authenticated.
 *
 * Error states:
 *   - Tour not found (404): redirect to home with flash error
 *   - Tour has no discussion_topic_id (a tour that does not play in the
 *     reader): redirect to home with flash error
 *   - Network/server error: redirect to home with flash error
 */

import { authorizationHeaders } from "@/services/sessionToken";
import { useNotificationsStore } from "@/stores/notifications";

/**
 * The workspace modules the guard reads and writes, loaded when the guard runs
 * rather than at boot: `router/index.js` imports this file statically, and no
 * module the entry reaches statically imports a workspace store (launch-
 * marketing-pages M2, the boot-graph rule). A guard runs on navigation, so the
 * load costs nothing a visitor to a public page pays.
 */
function loadTourWorkspace() {
  return Promise.all([
    import("@/stores/guidedTour"),
    import("@/stores/weRouting"),
    import("@/composables/useSecondaryPanelNavigation"),
  ]);
}

/**
 * Build headers for a v2 API request, mirroring apiVersioning.getRequestConfig().
 * We use raw fetch here because the Pinia store-based apiClient depends on
 * component context; navigation guards run outside component lifecycle.
 */
function buildApiHeaders() {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-API-Version": "v2",
  };

  // The session bearer, from its one owner (th-vfii1).
  Object.assign(headers, authorizationHeaders());

  const sessionId = localStorage.getItem("tower_hunt_session_id");
  if (sessionId) {
    headers["X-Session-ID"] = sessionId;
  }

  return headers;
}

/**
 * Open the reader on a linked tour that plays there (spec guided-tour-reader
 * "A link opens the reader"). The landing is a yield trigger ("The reader
 * minimizes when ... another surface takes the panel"): the current tour is
 * minimized first, kept at its page, and `play` then sets it aside and docks
 * the linked one, through its own venue gate and its one dock. The thread is
 * the outline's own (the viewer's topic or viewer thread), never minted here.
 *
 * Not awaited: playback reads the tour and enters its page after the route
 * has redirected to MainMap, so the landing never waits on a tour load.
 *
 * @param {object} guidedTour - The guided-tour store
 * @param {object} tour - The outline `GET /api/v2/guided_tours/:id` served
 */
function playLinkedTour(guidedTour, tour) {
  guidedTour.minimizeReader();
  guidedTour.play(tour.id, { threadId: tour.discussion_topic_id });
}

/**
 * beforeEnter guard for the /tours/:id route.
 *
 * Fetches the tour's outline. A tour that plays in the reader is played for
 * the viewer (`playLinkedTour`); any other tour's discussion_topic_id +
 * discussion_hub_id set weRouting state and switch to the We tab. Either way
 * the route redirects to MainMap.
 *
 * @param {import('vue-router').RouteLocationNormalized} to
 * @returns {import('vue-router').NavigationGuardReturn}
 */
export async function resolveTourRoute(to) {
  const tourId = to.params.id;

  try {
    // Fetch the tour (lightweight — we only need topic + hub IDs from the response)
    const response = await fetch(`/api/v2/guided_tours/${tourId}`, {
      method: "GET",
      headers: buildApiHeaders(),
    });

    if (!response.ok) {
      // 404 or access denied — show error and go home
      const notifications = useNotificationsStore();
      notifications.addDataProcessingNotification(
        "error",
        "Tour not found",
        "The tour you're looking for doesn't exist or you don't have access to it.",
        5,
      );
      return { name: "MainMap" };
    }

    const json = await response.json();
    const tour = json.data;
    const [
      { useGuidedTourStore, playsInReader },
      { useWeRoutingStore },
      { useSecondaryPanelNavigation },
    ] = await loadTourWorkspace();

    if (playsInReader(tour)) {
      playLinkedTour(useGuidedTourStore(), tour);
      return { name: "MainMap" };
    }

    // Verify the tour is embedded in a topic
    if (!tour.discussion_topic_id) {
      const notifications = useNotificationsStore();
      notifications.addDataProcessingNotification(
        "error",
        "Tour not available",
        "This tour isn't associated with a discussion topic yet.",
        5,
      );
      return { name: "MainMap" };
    }

    // Navigate to the topic via weRouting + secondary panel
    const weRouting = useWeRoutingStore();
    const { navigateToHorizontalTab } = useSecondaryPanelNavigation();

    if (tour.discussion_hub_id) {
      weRouting.selectHub(tour.discussion_hub_id);
    }
    weRouting.selectTopic(tour.discussion_topic_id);

    navigateToHorizontalTab("We", {
      contextIds: {
        hubId: tour.discussion_hub_id,
        topicId: tour.discussion_topic_id,
      },
      clearDynamicState: false,
    });

    // Redirect to MainMap — the We tab + topic state is already set above
    return { name: "MainMap" };
  } catch (err) {
    // Network error or unexpected failure
    console.error("[tourResolver] Failed to resolve tour:", err);
    const notifications = useNotificationsStore();
    notifications.addDataProcessingNotification(
      "error",
      "Tour not available",
      "Something went wrong while loading the tour. Please try again.",
      5,
    );
    return { name: "MainMap" };
  }
}
